import { cn, Icon, Tooltip, Typography } from "@kyan/ui";
import React from "react";

import {
  formatCurrency,
  formatPercent,
  formatValue,
} from "#/utils/valueFormatters";

import { LoadOrRender } from "../LoadOrRender/LoadOrRender";

export type MarketStatItemVariant =
  | "percentage"
  | "percentage-indicator"
  | "value"
  | "currency";

export interface MarketStatConfig {
  label: string;
  variant: MarketStatItemVariant;
  id?: string;
  tooltip?: React.ReactNode;
  precision?: number;
}

export interface MarketStatItemProps {
  config: MarketStatConfig;
  value: number | number[];
}

const formatItemValue = (
  variant: MarketStatItemVariant,
  value: number,
  precision = 2,
) => {
  switch (variant) {
    case "percentage":
    case "percentage-indicator":
      return formatPercent(value, {
        precision,
        trailingZeroDisplay: "stripIfInteger",
      });
    case "value":
      return formatValue(value, {
        precision,
        trailingZeroDisplay: "stripIfInteger",
      });
    case "currency":
      return formatCurrency(value, { precision });
    default:
      return value.toString();
  }
};

export const MarketStatItem: React.FC<MarketStatItemProps> = ({
  config: { label, variant, tooltip, precision },
  value = Number.NaN,
}) => {
  const formattedValue = Array.isArray(value)
    ? value.map((val) => formatItemValue(variant, val, precision)).join(" / ")
    : formatItemValue(variant, value, precision);
  const firstValue = Array.isArray(value) ? value[0] : value;
  const isNegativeChange = firstValue < 0;
  const isPositiveChange = firstValue > 0;
  const shouldShowLoadingIndicator = isNaN(firstValue);
  const isValueVariant = variant === "value";

  return (
    <div className="flex flex-col items-start gap-1">
      <Tooltip content={tooltip} disabled={!tooltip}>
        <button
          className={cn(
            "font-grotesk text-[11px] leading-none whitespace-nowrap",
            {
              "cursor-help underline": tooltip,
            },
          )}
        >
          {label}
        </button>
      </Tooltip>
      <LoadOrRender
        className="h-3 w-15 rounded-xs"
        isLoading={shouldShowLoadingIndicator}
      >
        <div className="flex items-center gap-1.5">
          <Typography
            xs
            primary
            className={cn("leading-none whitespace-nowrap", {
              "text-success": isPositiveChange && !isValueVariant,
              "text-error": isNegativeChange && !isValueVariant,
            })}
          >
            {formattedValue}
          </Typography>
          {variant === "percentage-indicator" && firstValue !== 0 && (
            <Icon
              name="triangle-up"
              className={cn("text-[5px]", {
                "text-success": !isNegativeChange,
                "text-error rotate-180": isNegativeChange,
              })}
            />
          )}
        </div>
      </LoadOrRender>
    </div>
  );
};
